import {extractIndoorMapLink} from "../util/util";

// Component to render location information safely
const LocationDisplay = ({parsedData, debugMode = false}) => {
    const {floor, room, otherLines} = parsedData;
    const visibleLines = debugMode
        ? otherLines
        : otherLines.filter((line) => !isDebugOnlyLine(line));
    const primaryLocationLine = visibleLines.find((line) => line.startsWith("Location:"));
    const remainingLines = primaryLocationLine
        ? visibleLines.filter((line) => line !== primaryLocationLine)
        : visibleLines;
    const detailLines = remainingLines.filter((line) => line.includes(":"));
    const plainLines = remainingLines.filter((line) => !line.includes(":"));

    return (
        <div className="text-slate-700">
            {(room || floor || primaryLocationLine) && <div className="mb-3 flex flex-col gap-2">
                {room && <InfoTile label="Room" value={room} tone="amber"/>}
                {floor && <InfoTile label="Floor" value={floor} tone="sky"/>}
                {primaryLocationLine && <InfoTile
                    label={extractLineLabel(primaryLocationLine)}
                    value={extractLineValue(primaryLocationLine)}
                    tone="slate"
                />}
            </div>}
            {detailLines.length > 0 && <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 shadow-sm">
                <div className="space-y-2">
                    {detailLines.map((line, index) => (
                        <DetailRow
                            key={`${line}-${index}`}
                            label={extractLineLabel(line)}
                            value={extractLineValue(line)}
                        />
                    ))}
                </div>
            </div>}
            {plainLines.length > 0 && <div className="mt-3 space-y-1.5 text-sm font-medium text-slate-500">
                {plainLines.map((line, index) => (
                    <div key={`${line}-${index}`}>
                        {line}
                    </div>
                ))}
            </div>}
        </div>
    );
};

const InfoTile = ({label, value, tone = "slate"}) => {
    const toneClassName = {
        amber: "border-amber-200 bg-amber-50 text-amber-900",
        sky: "border-sky-200 bg-sky-50 text-sky-900",
        slate: "border-slate-200 bg-slate-50 text-slate-900"
    }[tone] || "border-slate-200 bg-slate-50 text-slate-900";

    return <div className={`rounded-xl border px-3 py-2 shadow-sm ${toneClassName}`}>
        <div className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</div>
        <div className="mt-1 text-base font-semibold leading-tight">
            {value}
        </div>
    </div>;
};

const DetailRow = ({label, value}) => <div className="flex items-start justify-between gap-3 border-b border-slate-200/80 pb-2 last:border-b-0 last:pb-0">
    <div className="min-w-0 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-slate-400">
        {label}
    </div>
    <div className="min-w-0 flex-1 text-right text-sm font-semibold leading-snug text-slate-700">
        {value}
    </div>
</div>;

const TelemetryRow = ({icon, label, value}) => <div className="flex items-center justify-between gap-3 border-b border-sky-100/80 pb-2 last:border-b-0 last:pb-0">
    <div className="flex items-center text-sm font-semibold text-slate-700">
        {icon}
        <span className="uppercase tracking-[0.08em] text-sky-700">{label}</span>
    </div>
    <div className="text-sm font-semibold text-slate-700">
        {value}
    </div>
</div>;

function extractLineLabel(line = "") {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) {
        return "Location";
    }

    return line.slice(0, separatorIndex).trim();
}

function extractLineValue(line = "") {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) {
        return line;
    }

    return line.slice(separatorIndex + 1).trim();
}

const CallHistoryRawItem = ({clickAllowed, clickHandler, history, debugMode = false}) => {
    const {created, payload} = history;
    const parsedDate = new Date(Date.parse(created));
    const hasResolvedLocations = !!(payload && payload.length && payload[0].rankedLocations.length);
    const {
        gpsReportedLatitude,
        gpsReportedLongitude,
        gpsLatitude,
        gpsLongitude,
        batteryReading,
        gpsEstimated
    } = payload && payload.length && payload[0] ? payload[0] : {};
    const drift = gpsReportedLongitude && gpsReportedLatitude
        ? calcCrow(gpsReportedLatitude, gpsReportedLongitude, gpsLatitude, gpsLongitude)
        : null;
    return <div className={`min-w-0 w-full rounded-xl border border-slate-200 bg-white p-3 flex flex-col shadow-sm ${clickAllowed ? "cursor-pointer" : ""}`}
                onClick={clickAllowed ? clickHandler : null}>
        <div className="flex flex-row items-center justify-between">
            <span className="w-full ml-2 mb-1 font-medium text-sm text-slate-700">
                {parsedDate.toLocaleTimeString()}
            </span>
            {gpsEstimated && <span className="mb-1 mr-2 shrink-0">
                {MultiLaterationIcon}
            </span>}
        </div>
        <div className="">
            {hasResolvedLocations && payload[0].rankedLocations.map(location => {
                let hrl = location.humanReadableLocation;
                hrl = hrl.replace(/(\n)+/g, "\n");
                let {parsedData} = extractIndoorMapLink(hrl);
                const wifiIcon = location.connected ? <ConnectedIcon/> : <DisConnectedIcon/>;
                return <div key={`location-${location.confidence}-${location.tagUsed}`} className="p-2 flex flex-col">
                    <span className="ml-2">
                        <LocationDisplay parsedData={parsedData} debugMode={debugMode}/>
                    </span>
                    <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2.5 shadow-sm">
                        <div className="mb-2 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-blue-400">Signal</div>
                        <div className="flex items-center gap-4 text-sm font-semibold text-slate-700">
                            <div className="flex items-center">
                                {ConfidenceIcon}
                                <span>{location.confidence}</span>
                            </div>
                            <div className="flex items-center">
                                {wifiIcon}
                                {debugMode && <span className="ml-1">{location.tagUsed}</span>}
                            </div>
                        </div>
                    </div>
                </div>
            })}
            {!hasResolvedLocations && <span className="font-medium text-sm pl-2">No Resolved Locations</span>}
        </div>
        {debugMode && gpsLatitude && gpsLongitude && <>
            <div className="ml-1 text-slate-800 mt-3 mb-2 flex flex-row text-sm flex-wrap">
                {LocationIcon} {gpsLatitude + ", " + gpsLongitude}
            </div>
        </>
        }
        {(gpsEstimated || (drift !== null && drift !== 0) || batteryReading) && <div className="mt-4 rounded-xl border border-sky-100 bg-sky-50/40 px-3 py-2.5 shadow-sm">
            <div className="mb-2 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-sky-400">Telemetry</div>
            <div className="space-y-2">
                {!gpsEstimated && drift !== null && drift !== 0 && <TelemetryRow
                    icon={DriftIcon}
                    label="Drift"
                    value={`${drift} m`}
                />}
                {batteryReading && <TelemetryRow
                    icon={BatteryIcon}
                    label="Battery"
                    value={`${batteryReading} %`}
                />}
            </div>
        </div>}
    </div>;
};

export default CallHistoryRawItem;

function isDebugOnlyLine(line = "") {
    return line.startsWith("AP Lat/Lng:")
        || line.startsWith("AP Id:");
}

const ConfidenceIcon = <svg className="h-5 w-5 mr-1 text-blue-600" width="24" height="24" viewBox="0 0 24 24"
                            strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round"
                            strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z"/>
    <rect x="3" y="12" width="6" height="8" rx="1"/>
    <rect x="9" y="8" width="6" height="12" rx="1"/>
    <rect x="15" y="4" width="6" height="16" rx="1"/>
    <line x1="4" y1="20" x2="18" y2="20"/>
</svg>;
const DisConnectedIcon = () => <svg className="h-5 w-5 mr-1 text-blue-700" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23"/>
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
    <path d="M10.71 5.05A16 16 0 0 1 22.58 9"/>
    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
    <line x1="12" y1="20" x2="12.01" y2="20"/>
</svg>;
const ConnectedIcon = () => <svg className="h-6 w-6 pb-1 mr-1 text-blue-700" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor"
                                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
    <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
    <line x1="12" y1="20" x2="12.01" y2="20"/>
</svg>;
const LocationIcon = <svg className="h-4 w-4 mr-2 text-blue-700" width="24" height="24" viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z"/>
    <path d="M21 3L14.5 21a.55 .55 0 0 1 -1 0L10 14L3 10.5a.55 .55 0 0 1 0 -1L21 3"/>
</svg>;
const BatteryIcon = <svg className="pb-1 h-7 w-7 mr-1 text-blue-700" width="24" height="24" viewBox="0 0 24 24"
                         strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round"
                         strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z"/>
    <path
        d="M6 7h11a2 2 0 0 1 2 2v.5a.5 .5 0 0 0 .5 .5a.5 .5 0 0 1 .5 .5v3a.5 .5 0 0 1 -.5 .5a.5 .5 0 0 0 -.5 .5v.5a2 2 0 0 1 -2 2h-11a2 2 0 0 1 -2 -2v-6a2 2 0 0 1 2 -2"/>
    <line x1="7" y1="10" x2="7" y2="14"/>
    <line x1="10" y1="10" x2="10" y2="14"/>
    <line x1="13" y1="10" x2="13" y2="14"/>
</svg>;
const MultiLaterationIcon = <svg className="h-6 w-6 mr-1 text-sky-700" width="24" height="24" viewBox="0 0 24 24"
                                 strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round"
                                 strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z"/>
    <circle cx="6" cy="18" r="2"/>
    <circle cx="18" cy="18" r="2"/>
    <circle cx="12" cy="6" r="2"/>
    <path d="M7.7 16.9l2.8 -8.1"/>
    <path d="M16.3 16.9l-2.8 -8.1"/>
    <path d="M8 18h8"/>
</svg>;
const DriftIcon = <svg width="24" height="24"
                       className="h-7 w-7 pb-1 mr-1 text-blue-700"
                       viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"
                       strokeWidth="3" stroke="#000000" fill="none">
    <path
        d="M17.94,54.81a.1.1,0,0,1-.14,0c-1-1.11-11.69-13.23-11.69-21.26,0-9.94,6.5-12.24,11.76-12.24,4.84,0,11.06,2.6,11.06,12.24C28.93,41.84,18.87,53.72,17.94,54.81Z"/>
    <circle cx="17.52" cy="31.38" r="4.75"/>
    <path
        d="M49.58,34.77a.11.11,0,0,1-.15,0c-.87-1-9.19-10.45-9.19-16.74,0-7.84,5.12-9.65,9.27-9.65,3.81,0,8.71,2,8.71,9.65C58.22,24.52,50.4,33.81,49.58,34.77Z"/>
    <circle cx="49.23" cy="17.32" r="3.75"/>
    <path d="M17.87,54.89a28.73,28.73,0,0,0,3.9.89"/>
    <path d="M24.68,56.07c2.79.12,5.85-.28,7.9-2.08,5.8-5.09,2.89-11.25,6.75-14.71a16.72,16.72,0,0,1,4.93-3"
          strokeDasharray="7.8 2.92"/>
    <path d="M45.63,35.8a23,23,0,0,1,3.88-.95"/>
</svg>

function calcCrow(lat1, lon1, lat2, lon2) {
    const R = 6371; // km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const lat1Rad = toRad(lat1);
    const lat2Rad = toRad(lat2);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1Rad) * Math.cos(lat2Rad);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c * 1000);
}

// Converts numeric degrees to radians
function toRad(Value) {
    return Value * Math.PI / 180;
}
